import { useEffect, useRef, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BankDetails, Breadcrumbs, Table, UserDetails } from "../components";
import { useBank, useCustomer } from "../hooks";
import useApiService from "../hooks/useApiService";

const CustomersPage = () => {
  const tableHeaderColumns = [
    { id: "customer_name", name: "Our Customers" },
    { id: "customer_contact", name: "Contact number" },
    { id: "total_client_payment", name: "Total client payment" },
    { id: "total_paid", name: "Total paid" },
    { id: "client_payment_due_now", name: "Client payment due now" },
    { id: "total_upcoming", name: "Total upcoming" },
    { id: "action", name: "More Details" },
  ];
  const plotsBoughtTableColumns = [
    { id: "project_name", name: "Project Name" },
    { id: "phase_number", name: "Phase Number" },
    { id: "plot_number", name: "Plot Number" },
    { id: "agent_name", name: "Agent Name" },
    { id: "bought_date", name: "Bought Date" },
    { id: "action", name: "More Details" },
  ];

  const [customers, setCustomers] = useState([]);
  const [searchParams] = useSearchParams();
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const { fetchAllCustomers, fetchCustomerById } = useCustomer();
  const { addBankAccount, updateBankDetails } = useBank();

  useEffect(() => {
    const id = searchParams.get("id");
    if (id) setSelectedCustomerId(id);
  }, [searchParams]);

  // Fetch List of all customers
  useEffect(() => {
    const fetchCustomers = async () => {
      const { customers, error } = await fetchAllCustomers();
      if (error) return toast.error(error);
      setCustomers(customers);
    };
    fetchCustomers();
  }, []);

  const [customerBank, setCustomerBank] = useState({});
  const [customerDetails, setCustomerDetails] = useState({});
  const [customerPlotsBought, setCustomerPlotsBought] = useState({});

  // Fetch details of selected customer
  const fetchCustomer = async () => {
    const { customer, error } = await fetchCustomerById(selectedCustomerId);
    if (error) return toast.error(error);
    setCustomerDetails(customer.customer);
    setCustomerBank(customer.bank);
    setCustomerPlotsBought(customer.plots);
  };

  useEffect(() => {
    if (selectedCustomerId) fetchCustomer();
  }, [selectedCustomerId]);

  const navigate = useNavigate();
  const handleBookingDetailsViewAction = (id) => {
    navigate(`/bookings?id=${id}&customer=${selectedCustomerId}`);
  };

  const { put } = useApiService();
  const handleEditCustomer = async (editedData) => {
    try {
      const response = await put(`/api/customers/${selectedCustomerId}`, editedData);
      if (response.status) {
        fetchCustomer();
      } else {
        throw new Error("Unable to update customer");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddCustomerBankDetails = async (newData) => {
    const data = {
      ...newData,
      bank_identification_id: newData.bank_identification,
      bank_branch_name: newData.bank_branch,
    };

    const { success, error } = addBankAccount("customer", selectedCustomerId, data);
    if (error) return toast.error(error);
    toast.success(success);
    fetchCustomer();
  };

  const handleEditCustomerBankDetails = async (editedData) => {
    const dataToUpdate = { ...editedData };
    const bankId = customerBank.id;
    if (Object.keys(editedData).includes("bank_branch")) {
      dataToUpdate.bank_branch_name = editedData.bank_branch;
    }
    if (Object.keys(editedData).includes("bank_identification")) {
      dataToUpdate.bank_identification_id = editedData.bank_identification;
    }
    const { success, error } = await updateBankDetails(bankId, dataToUpdate);
    if (error) return toast.error(error);
    toast.success(success);
    fetchCustomer();
  };

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("edit_customer_data"));
    if (data) {
      setCustomerDetails((prev) => ({ ...prev, ...data }));
    }
  }, []);

  const detailsRef = useRef();
  useEffect(() => {
    if (selectedCustomerId && detailsRef.current)
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [selectedCustomerId]);

  return (
    <>
      <Breadcrumbs />
      <div className="row">
        <div className="table_title">Customers</div>
        <div className="count">Total: {customers.length} customers</div>
      </div>
      <Table columns={tableHeaderColumns} data={customers} setSelectedId={setSelectedCustomerId} />
      {selectedCustomerId && (
        <main className="section_wrapper" ref={detailsRef}>
          <UserDetails
            parentName="Customer"
            showEditButton
            data={customerDetails}
            handleEdit={handleEditCustomer}
            handleAadharUpload={(imageId, url) => handleEditCustomer({ aadhar_document_id: imageId })}
            handlePanUpload={(imageId, url) => handleEditCustomer({ pan_document_id: imageId })}
            title={customerDetails.name ?? "Customer Details"}
          />
          <BankDetails
            parentName="Customer"
            // showEditButton
            data={customerBank.id && customerBank}
            handleAdd={handleAddCustomerBankDetails}
            // handleEdit={handleEditCustomerBankDetails}
            handleIdentificationUpload={(imageId, url) =>
              handleEditCustomerBankDetails({ bank_identification_id: imageId })
            }
            noEdit={true}
          />
          <section className="section_wrapper">
            <section className="section_title_wrapper">
              <h2 className="section_title">Plots bought by {customerDetails.name}</h2>
              <span>
                Total Plots Bought - {customerPlotsBought.count}
                {customerPlotsBought.count === 1 ? " Plot" : " Plots"}
              </span>
            </section>
            {customerPlotsBought.plots_bought && (
              <Table
                columns={plotsBoughtTableColumns}
                data={customerPlotsBought.plots_bought}
                handleAction={handleBookingDetailsViewAction}
              />
            )}
          </section>
        </main>
      )}
    </>
  );
};

export default CustomersPage;
